// App entry point
// In your main.tsx or index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './app/App';
import './i18n/config'; // Initialize i18n
import '@/styles/index.css';
import { startSessionKeepAlive, setOrgSessionPolicy } from '@/lib/sessionKeepAlive';
import { setOrgFormatPrefs } from '@/lib/orgFormat';
import { refreshAccessToken } from '@/lib/authRefresh';
import { getCsrf } from '@/lib/session';
// Side-effect import: registers the beforeinstallprompt listener before React
// renders. Chrome fires it once, early; a listener added when a component
// mounts is too late and the event is lost.
import '@/features/shared/components/install/installCapture';
// Registers the service worker. Activation is never automatic: see
// registerWorker.ts for why there is no reload on controllerchange.
import { syncServiceWorker, registrationDiagnostics } from '@/features/shared/components/recovery/registerWorker';
import { store } from '@/app/store';
import { tenantPathSegment, isTenantId, type TenantLike } from '@/features/auth/utils/tenantPath';
import { installLastRequestRecorder } from '@/lib/clientErrorReport';

// Remember the last API request (method, path, status, error code — never a
// body, query string or token) so a crash screen can say what had just
// answered. See clientErrorReport.ts.
installLastRequestRecorder();

// Build marker — bump to force a fresh entry-chunk hash when the CDN edge cached a
// poisoned response under an immutable /assets/* URL (see public/_redirects). A new hash
// is a never-seen URL, so no browser/edge cache can serve a stale/poisoned copy.
const BUILD_MARKER = 'kp-2026-08-02-cachebust-1';
(window as unknown as { __KP_BUILD__?: string }).__KP_BUILD__ = BUILD_MARKER;

// Keep the session alive while the user is active (and honor their idle
// sign-out preference). Gated on the persisted identity so it no-ops on the
// login page; on idle timeout, revoke server-side then land on login.
startSessionKeepAlive({
  refresh: refreshAccessToken,
  isAuthed: () => !!localStorage.getItem('user_data'),
  signOut: () => {
    fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || ''}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': getCsrf() ?? '' },
    })
      .catch(() => undefined)
      .finally(() => window.location.assign('/login?expired=1'));
  },
});

// Org session policy (Organization Settings → Security → session timeout): fetched
// once per load for signed-in members; the localStorage copy applies instantly on
// the next load. Failure keeps the last-known policy — never loosens on an error.
if (localStorage.getItem('user_data')) {
  fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || ''}/api/v1/tenants/me/security-policy`, {
    credentials: 'include',
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((p) => {
      if (p && typeof p.sessionTimeoutEnforced === 'boolean') setOrgSessionPolicy(p);
    })
    .catch(() => undefined);
}

// Org display prefs (date format, week start) for the shared formatters.
// NOT gated on the `user_data` localStorage key: the active login flow doesn't
// reliably set it (it's cookie/redux-persist based), so a `user_data` gate left
// this fetch dead → the org date format never loaded and every formatter fell
// back to the DD/MM/YYYY default regardless of the configured format (P1 regression).
// Auth is by the session cookie (credentials:include); logged out → 401 → the
// catch keeps defaults. The result caches to localStorage for instant startup.
fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || ''}/api/v1/tenants/me/org-preferences`, {
  credentials: 'include',
})
  .then((r) => (r.ok ? r.json() : null))
  .then((p) => {
    if (p && p.dateFormat) setOrgFormatPrefs({ dateFormat: p.dateFormat, weekStart: p.weekStart });
  })
  .catch(() => undefined);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// The pilot gate. Default OFF: a worker only reaches a tenant named in
// VITE_PWA_PILOT_TENANTS, because a registered worker cannot be withdrawn by
// reverting a deploy. The tenant is unknown until someone signs in, so this
// runs again whenever the workspace changes — which is also how a tenant
// REMOVED from the pilot gets its worker taken away on the next visit.
{
  // ⚠️ Use the app's own resolver, not `.slug`. The session's tenant object
  // frequently has NO slug field at all — it carries `subdomain` — so reading
  // slug directly resolved to null for every real workspace and the pilot
  // gate registered nobody, anywhere. Driving it on dev found that; the unit
  // tests had passed because they supplied a slug the real object lacks.
  // A bare tenant id is not a workspace name and can never match the pilot
  // list, so it is treated as unknown rather than fed in as a tenant.
  const slugOf = () => {
    const t = (store.getState() as { tenant?: { current?: TenantLike | null } }).tenant?.current;
    const seg = tenantPathSegment(t);
    return seg && !isTenantId(seg) ? seg : null;
  };
  let last: string | null | undefined;
  const apply = () => {
    const slug = slugOf();
    if (slug === last) return;
    last = slug;
    void syncServiceWorker(slug);
  };
  apply();
  store.subscribe(apply);

  // ⚠️ A deliberate, read-only diagnostic hook. "Desktop notifications do
  // not arrive" was impossible to diagnose from a running browser: the gate's
  // decision was never recorded and register()'s error was thrown away, so
  // four very different causes all presented as "zero registrations".
  //
  // Returns a copy, holds no personal data (a workspace slug, a decision, an
  // error string), and changes no behaviour. Support can ask for
  // `__kpServiceWorker()` instead of asking someone to read minified source.
  (window as unknown as { __kpServiceWorker?: () => unknown }).__kpServiceWorker =
    registrationDiagnostics;
}
